const KEYBOARD_EVENT_TYPES = new Set(["keydown", "keyup", "keypress"]);

function captureValue(options) {
  return typeof options === "boolean" ? options : Boolean(options?.capture);
}

function shouldBypassUnityKeyboard(event) {
  const target = event?.target;
  return target instanceof Element && Boolean(
    target.closest('[data-unity-keyboard-bypass="true"]')
  );
}

function invokeListener(listener, receiver, event) {
  if (typeof listener === "function") return listener.call(receiver, event);
  return listener?.handleEvent?.(event);
}

export function beginUnityKeyboardListenerCapture(
  targets = [window, document]
) {
  const records = [];
  const originalAddMethods = new Map();
  let addMethodsRestored = false;

  targets.forEach((target) => {
    const originalAdd = target.addEventListener;
    originalAddMethods.set(target, originalAdd);

    target.addEventListener = function captureKeyboardListener(type, listener, options) {
      if (
        KEYBOARD_EVENT_TYPES.has(type) &&
        listener &&
        !records.some((record) =>
          record.target === target &&
          record.type === type &&
          record.listener === listener &&
          captureValue(record.options) === captureValue(options)
        )
      ) {
        records.push({ target, type, listener, options });
      }
      return originalAdd.call(this, type, listener, options);
    };
  });

  const restoreAddMethods = () => {
    if (addMethodsRestored) return;
    addMethodsRestored = true;
    originalAddMethods.forEach((originalAdd, target) => {
      target.addEventListener = originalAdd;
    });
  };

  return {
    cancel() {
      restoreAddMethods();
    },
    installGuards() {
      restoreAddMethods();
      const guardedListeners = records.map(({ target, type, listener, options }) => {
        target.removeEventListener(type, listener, options);
        const guardedListener = function guardEditableTarget(event) {
          if (shouldBypassUnityKeyboard(event)) return undefined;
          return invokeListener(listener, this, event);
        };
        target.addEventListener(type, guardedListener, options);
        return { target, type, guardedListener, options };
      });

      return () => {
        guardedListeners.forEach(({ target, type, guardedListener, options }) => {
          target.removeEventListener(type, guardedListener, options);
        });
      };
    },
  };
}
